import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  AffiliateByPhoneNumberQuery,
  useTransformLendingTreeLeadToDealMutation,
} from '../../gql/generated/graphql';

import Modal from '../shared/Modal';

import { logger } from '../../libs/Logger';

type AffiliateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  affiliateData?: AffiliateByPhoneNumberQuery['affiliateByPhoneNumber'] | null;
};

const AffiliateModal = ({ isOpen, onClose, affiliateData }: AffiliateModalProps) => {
  const history = useHistory();
  const [transformLead, { loading }] = useTransformLendingTreeLeadToDealMutation();

  if (!affiliateData) {
    return null;
  }

  const handleCreateDeal = async () => {
    try {
      const result = await transformLead({
        variables: {
          affiliate: {
            external_id: affiliateData.external_id,
            decision_accepted: affiliateData.decision_accepted,
            affiliate_provider_id: affiliateData.affiliate_provider_id,
            decision_response: affiliateData.decision_response,
            created_at: affiliateData.created_at,
            ssn_id: affiliateData.ssn_id,
            lead_request: affiliateData.lead_request,
            updated_at: affiliateData.updated_at,
            id: affiliateData.id,
          },
        },
      });

      if (result.data?.transformLendingTreeLeadToDeal) {
        const deal = result.data.transformLendingTreeLeadToDeal;
        toast.success(`Deal created successfully! Deal ID: ${deal.id}`);
        onClose();
        history.push(`/deals/${deal.id}`);
      }
    } catch (error) {
      toast.error('Failed to create deal');
      logger.error('AffiliateModal.tsx', 'Error creating deal:', null, error);
    }
  };

  const vehicleInfo = affiliateData.lead_request?.autoLoan?.vehicleInfo;
  const firstOffer = affiliateData.decision_response?.offers?.[0];

  return (
    <Modal
      title="Lending Tree Lead - From Alex"
      isOpen={isOpen}
      onClose={onClose}
      canDismiss={false}
      closeOnOverlayClick={false}
      size="4xl"
      rightButtons={
        <Button
          variant="primary"
          onClick={handleCreateDeal}
          isLoading={loading}
          loadingText="Creating Deal..."
        >
          Create Deal
        </Button>
      }
    >
      <HStack spacing={0} align="stretch" p={4}>
        {/* Consumer Information */}
        <Box flex={1} minW="200px" pr={6} borderRight="1px solid" borderColor="gray.300">
          <Text fontSize="md" fontWeight="semibold" mb={3} color="gray.700">
            Consumer Information
          </Text>
          <VStack spacing={2} align="stretch">
            {affiliateData.lead_request?.consumer?.contact?.firstName && (
              <HStack>
                <Text fontSize="sm" fontWeight="medium" minW="120px" color="gray.600">
                  First Name:
                </Text>
                <Text fontSize="sm">{affiliateData.lead_request.consumer.contact.firstName}</Text>
              </HStack>
            )}
            {affiliateData.lead_request?.consumer?.contact?.lastName && (
              <HStack>
                <Text fontSize="sm" fontWeight="medium" minW="120px" color="gray.600">
                  Last Name:
                </Text>
                <Text fontSize="sm">{affiliateData.lead_request.consumer.contact.lastName}</Text>
              </HStack>
            )}
            {affiliateData.lead_request?.consumer?.contact?.primaryPhone && (
              <HStack>
                <Text fontSize="sm" fontWeight="medium" minW="120px" color="gray.600">
                  Primary Phone:
                </Text>
                <Text fontSize="sm">
                  {affiliateData.lead_request.consumer.contact.primaryPhone}
                </Text>
              </HStack>
            )}
          </VStack>
        </Box>

        {/* Vehicle Information */}
        <Box
          flex={1}
          minW="200px"
          px={6}
          borderRight={firstOffer ? '1px solid' : 'none'}
          borderColor="gray.300"
        >
          <Text fontSize="md" fontWeight="semibold" mb={3} color="gray.700">
            Vehicle Information
          </Text>
          <VStack spacing={2} align="stretch">
            {vehicleInfo?.year && (
              <HStack>
                <Text fontSize="sm" fontWeight="medium" minW="120px" color="gray.600">
                  Year:
                </Text>
                <Text fontSize="sm">{vehicleInfo.year}</Text>
              </HStack>
            )}
            {vehicleInfo?.make && (
              <HStack>
                <Text fontSize="sm" fontWeight="medium" minW="120px" color="gray.600">
                  Make:
                </Text>
                <Text fontSize="sm">{vehicleInfo.make}</Text>
              </HStack>
            )}
            {vehicleInfo?.model && (
              <HStack>
                <Text fontSize="sm" fontWeight="medium" minW="120px" color="gray.600">
                  Model:
                </Text>
                <Text fontSize="sm">{vehicleInfo.model}</Text>
              </HStack>
            )}
            {vehicleInfo?.mileage != null && (
              <HStack>
                <Text fontSize="sm" fontWeight="medium" minW="120px" color="gray.600">
                  Mileage:
                </Text>
                <Text fontSize="sm">{vehicleInfo.mileage.toLocaleString()}</Text>
              </HStack>
            )}
          </VStack>
        </Box>

        {/* Offer Information */}
        {firstOffer && (
          <Box flex={1} minW="200px" pl={6}>
            <Text fontSize="md" fontWeight="semibold" mb={3} color="gray.700">
              First Offer
            </Text>
            <VStack spacing={2} align="stretch">
              {firstOffer.amount != null && (
                <HStack>
                  <Text fontSize="sm" fontWeight="medium" minW="120px" color="gray.600">
                    Amount:
                  </Text>
                  <Text fontSize="sm">${firstOffer.amount.toLocaleString()}</Text>
                </HStack>
              )}
              {firstOffer.term != null && (
                <HStack>
                  <Text fontSize="sm" fontWeight="medium" minW="120px" color="gray.600">
                    Term:
                  </Text>
                  <Text fontSize="sm">{firstOffer.term} months</Text>
                </HStack>
              )}
              {firstOffer.apr != null && (
                <HStack>
                  <Text fontSize="sm" fontWeight="medium" minW="120px" color="gray.600">
                    APR:
                  </Text>
                  <Text fontSize="sm">{firstOffer.apr.toFixed(2)}%</Text>
                </HStack>
              )}
              {firstOffer.monthlyPayment != null && (
                <HStack>
                  <Text fontSize="sm" fontWeight="medium" minW="120px" color="gray.600">
                    Monthly Payment:
                  </Text>
                  <Text fontSize="sm">${firstOffer.monthlyPayment.toLocaleString()}</Text>
                </HStack>
              )}
            </VStack>
          </Box>
        )}
      </HStack>
    </Modal>
  );
};

export default AffiliateModal;
